import React, { useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import { BANNER_CONFIG, BannerVariant } from "@/constants/app.constant";

/**
 * Props for the Banner component
 */
export interface BannerProps {
  /** Visual variant of the banner. Determines background and text colors. Defaults to "maintenance". */
  variant?: BannerVariant;
  /** Content to display inside the banner */
  children: React.ReactNode;
  /** Whether the banner can be dismissed by the user. Defaults to true. */
  dismissible?: boolean;
  /** Callback function called when the banner is dismissed */
  onDismiss?: () => void;
  /** Additional CSS classes to apply to the banner container */
  className?: string;
}

/**
 * Banner component displays a dismissible alert banner with customizable variants.
 * Supports different visual styles (maintenance, info, success, warning, error) and
 * can be configured to be dismissible or persistent.
 * 
 * @component
 * @param {BannerProps} props - Component props
 * @param {BannerVariant} [props.variant="maintenance"] - Visual variant: "maintenance" | "info" | "success" | "warning" | "error"
 * @param {React.ReactNode} props.children - Content to display inside the banner
 * @param {boolean} [props.dismissible=true] - Whether the banner can be dismissed
 * @param {() => void} [props.onDismiss] - Callback fired when banner is dismissed
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element | null} Rendered banner or null if dismissed
 * 
 * @example
 * ```tsx
 * <Banner variant="info" dismissible onDismiss={() => undefined}>
 *   This is an informational message
 * </Banner>
 * ```
 * 
 * @example
 * ```tsx
 * <Banner variant="warning" dismissible={false}>
 *   Important: System maintenance scheduled
 * </Banner>
 * ```
 */
const Banner: React.FC<BannerProps> = ({
  variant = "maintenance",
  children,
  dismissible = true,
  onDismiss,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const config = BANNER_CONFIG[variant];

  return (
    <div
      className={cn(
        "w-full flex items-center justify-between px-6 py-3 md:text-sm text-xs",
        config.bg,
        config.text,
        className
      )}
      role="alert"
    >
      <div className="flex-1 p-3">{children}</div>

      {dismissible && (
        <button
          onClick={handleDismiss}
          className={cn("p-1 rounded transition-colors", config.closeHover)}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default Banner;
